import { useRouter } from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { useEffect } from 'react'
import type { ReactNode } from 'react'

import { userAtom } from '@/stores/userStore'

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID

// GA4 forbids PII in user_id — hash the email so we get a stable, non-PII identifier.
async function hashEmail(email: string): Promise<string> {
  const data = new TextEncoder().encode(email.trim().toLowerCase())
  const buffer = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

declare global {
  interface Window {
    dataLayer: unknown[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: (...args: any[]) => void
  }
}

if (GA_MEASUREMENT_ID) {
  // Load gtag.js
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  // Disable auto page_view; the router subscription fires it on every navigation
  // (including initial load via onResolved) so SPA route changes are tracked.
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false })
}

function GoogleAnalyticsPageTracker() {
  const router = useRouter()

  useEffect(() => {
    return router.subscribe('onResolved', (event) => {
      if (!window.gtag) return
      window.gtag('event', 'page_view', {
        page_path: event.toLocation.href,
        page_location: window.location.href,
        page_title: document.title,
      })
    })
  }, [router])

  return null
}

function GoogleAnalyticsIdentifier() {
  const user = useAtomValue(userAtom)

  useEffect(() => {
    if (!window.gtag || !user?.email) return

    let cancelled = false
    hashEmail(user.email).then((userId) => {
      if (cancelled || !window.gtag) return
      // user_id must be non-PII per GA4 ToS — we send the email hash.
      window.gtag('set', { user_id: userId })
      window.gtag('set', 'user_properties', {
        company: user.company,
        country: user.country,
      })
    })

    return () => {
      cancelled = true
    }
  }, [user])

  return null
}

export default function GoogleAnalyticsProvider({ children }: { children: ReactNode }) {
  if (!GA_MEASUREMENT_ID) {
    return <>{children}</>
  }

  return (
    <>
      <GoogleAnalyticsPageTracker />
      <GoogleAnalyticsIdentifier />
      {children}
    </>
  )
}
